// src/pages/Verification/VerificationPage.tsx
import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Skeleton, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';

import { useAppDispatch } from '../../configuration/redux/hooks';
import { openStripeOnboardingLink, startUserKyc } from '../Categories/actions';
import { getUserDetails } from '../Auth/actions';

import {
  ButtonTertiaryIcon,
  GlassButton,
  GlassColors,
  GlassShadows,
} from '../../components/glassmorphism/GlassmorphismStyles';
import { HandymanAppState } from '../../configuration/redux/reducers';
import { useSelector } from 'react-redux';

import { ReactComponent as LinkIcon } from '../../assets/icons/info/link.svg';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import Link from '@mui/material/Link';

const kycColor = (status?: string, globalStatus?: string | null) => {
  if (status === 'Verified') return '#29A322';
  if (
    (status === 'Pending' && globalStatus !== 'NotVerified') ||
    status === 'Review'
  )
    return '#D1AB01';
  if (status === 'Failed' || globalStatus === 'NotVerified') return '#C40003';
  return undefined;
};

const SkeletonBlock = () => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: 1,
      boxShadow: GlassShadows.menuMobile,
      p: '24px',
      borderRadius: '24px',
    }}
  >
    {/* Title */}
    <Skeleton variant='text' width='60%' height={32} />

    {/* Paragraph */}
    <Skeleton variant='text' />
    <Skeleton variant='text' />
    <Skeleton variant='text' width='85%' />

    {/* Bottom row */}
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Skeleton variant='circular' width={20} height={20} />
        <Skeleton variant='text' width={120} />
      </Box>

      <Skeleton variant='rounded' width={140} height={40} />
    </Box>
  </Box>
);

const VerificationPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const navigate = useNavigate();
  const location = useLocation();
  const userDetails = useSelector((s: HandymanAppState) => s.user.userDetails);
  const authData = useSelector((s: HandymanAppState) => s.user.authData);
  const userDetailsLoading = useSelector(
    (s: HandymanAppState) => s.user.isLoading,
  );

  const showSkeleton = !userDetails && !!authData ? true : userDetailsLoading;

  useEffect(() => {
    if (authData) {
      dispatch(getUserDetails() as any);
    }
  }, [authData, dispatch]);

  const [kycBusy, setKycBusy] = useState(false);
  const [stripeBusy, setStripeBusy] = useState(false);

  const handleKycClick = async () => {
    try {
      setKycBusy(true);
      const res = await dispatch(startUserKyc() as any);
      if (!res) return;
      window.open(res.shareableUrl!, '_blank', 'noopener,noreferrer');
    } finally {
      setKycBusy(false);
    }
  };

  const handleStripeClick = async () => {
    try {
      setStripeBusy(true);
      await dispatch(openStripeOnboardingLink() as any);
    } finally {
      setStripeBusy(false);
    }
  };

  const kycBtnText =
    userDetails?.kycStatus === 'Pending' &&
    userDetails?.verificationKycStatus !== 'NotVerified'
      ? 'Continue'
      : userDetails?.kycStatus === 'Failed' ||
          userDetails?.verificationKycStatus === 'NotVerified'
        ? 'Try Again'
        : userDetails?.kycStatus === 'Verified'
          ? 'Done'
          : 'Verify Now';

  return (
    <Box
      sx={{
        px: { xs: '12px', sm: '24px' },
        py: { xs: '32px', sm: '24px' },
        maxWidth: '956px',
        m: 'auto',
        position: { xs: 'relative', tablet: 'static' },
      }}
    >
      <Box
        sx={{
          borderRadius: { xs: 0, tablet: '32px' },
          boxShadow: { xs: 'none', tablet: GlassShadows.menuMobile },
          p: { xs: 0, tablet: '32px 16px 48px' },
          position: { xs: 'static', tablet: 'relative' },
        }}
      >
        <ButtonTertiaryIcon
          onClick={() => {
            navigate(-1);
          }}
          sx={{
            position: 'absolute',
            zIndex: 1,
            top: '32px',
            right: '32px',
            width: { xs: '40px' },
            height: { xs: '40px' },
            '@media (min-width:320px) and (max-width:400px)': {
              top: '16px',
              right: '16px',
              width: { xs: '40px' },
              height: { xs: '40px' },
              '& svg': {
                fontSize: '16px',
              },
            },
            '& svg': {
              fontSize: { xs: '24px' },
            },
          }}
        >
          <CloseIcon />
        </ButtonTertiaryIcon>
        <Box
          sx={{
            maxWidth: '760px',
            margin: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: '16px', tablet: '24px' },
          }}
        >
          <Typography
            component='h2'
            sx={{
              fontFamily: 'Montserrat',
              fontWeight: 600,
              fontSize: '34px',
              lineHeight: '124%',
              letterSpacing: '.15px',
              color: isDark ? GlassColors.accent : GlassColors.secondary,
              textAlign: 'center',
              maxWidth: { xs: '295px', sm: '495px', md: 'none' },
              m: 'auto',
            }}
          >
            Verify Your Details in 2&nbsp;Easy Steps
          </Typography>

          {/* KYC */}

          {showSkeleton ? (
            <SkeletonBlock />
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                boxShadow: GlassShadows.menuMobile,
                p: '24px',
                borderRadius: '24px',
              }}
            >
              <Typography
                component='h4'
                sx={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontWeight: 800,
                  fontSize: 20,
                  lineHeight: '160%',
                  letterSpacing: '.15px',
                  textShadow: '0 1px 1px rgba(0, 0, 0, 0.15)',
                  color:
                    kycColor(
                      userDetails?.kycStatus,
                      userDetails?.verificationKycStatus,
                    ) || (isDark ? GlassColors.accent : GlassColors.secondary),
                }}
              >
                {(() => {
                  if (userDetails?.verificationKycStatus === 'NotVerified') {
                    return 'KYC Verification Failed';
                  }

                  switch (userDetails?.kycStatus) {
                    case 'Pending':
                      return 'Your KYC verification in progress';
                    case 'Verified':
                      return 'You have successfully completed KYC verification!';
                    case 'Failed':
                      return 'KYC Verification Failed';
                    case 'NotStarted':
                      return 'Identity Verification (KYC)';
                    default:
                      return 'Identity Verification (KYC)';
                  }
                })()}
              </Typography>
              <Typography
                component='p'
                sx={{
                  fontSize: 16,
                  fontWeight: 400,
                  fontFamily: 'Roboto, sans-serif',
                  lineHeight: '150%',
                  letterSpacing: '.15px',
                  color: isDark
                    ? GlassColors.dark.textSecondary
                    : GlassColors.light.textSecondary,
                }}
              >
                KYC stands for "Know Your Customer." It is a standard identity
                verification process required by financial regulations to ensure
                all payments on our platform are secure and to prevent fraud.
                You will be asked to provide a valid government-issued ID and
                possibly other documents. This process is handled by our secure,
                certified partners to protect your sensitive information and
                allow you to receive payments safely.
              </Typography>

              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    p: '5.5px 16px',
                    '& svg path': {
                      fill: isDark
                        ? GlassColors.dark.textSecondary
                        : GlassColors.light.textSecondary,
                    },
                  }}
                >
                  <LinkIcon />
                  <Link
                    component={RouterLink}
                    to='/help-blog/identity-verification-kyc'
                    sx={{
                      fontFamily: 'Montserrat, sans-serif',
                      fontWeight: 500,
                      fontSize: 14,
                      lineHeight: 1.2,
                      color: isDark
                        ? GlassColors.dark.textSecondary
                        : GlassColors.light.textSecondary,
                      '&:before': {
                        opacity: 1,
                        backgroundColor: isDark
                          ? GlassColors.dark.textSecondary
                          : GlassColors.light.textSecondary,
                      },
                    }}
                  >
                    What is KYC
                  </Link>
                </Box>
                <GlassButton
                  disabled={userDetails?.kycStatus === 'Verified'}
                  onClick={
                    !!authData
                      ? () => handleKycClick()
                      : () =>
                          navigate('/sign-in', {
                            state: { from: location.pathname },
                          })
                  }
                  sx={{
                    '&&': {
                      height: '40px',
                      p: '6px 16px',
                      fontSize: 14,
                    },
                  }}
                >
                  {kycBusy ? (
                    <CircularProgress size={20} sx={{ color: '#E2F7FF' }} />
                  ) : (
                    <Typography
                      sx={{
                        fontFamily: 'Montserrat, sans-serif',
                        fontSize: 14,
                        fontWeight: 500,
                      }}
                    >
                      {!!authData ? kycBtnText : 'Log in to verify'}
                    </Typography>
                  )}
                </GlassButton>
              </Box>
            </Box>
          )}

          {/* STRIPE */}
          {authData?.role !== 'customer' &&
            (showSkeleton ? (
              <SkeletonBlock />
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  boxShadow: GlassShadows.menuMobile,
                  p: '24px',
                  borderRadius: '24px',
                }}
              >
                <Typography
                  component='h4'
                  sx={{
                    fontFamily: 'Montserrat, sans-serif',
                    fontWeight: 800,
                    fontSize: 20,
                    lineHeight: '160%',
                    letterSpacing: '.15px',
                    textShadow: '0 1px 1px  rgba(0, 0, 0, 0.15)',
                    color: userDetails?.isStripeAccountVerified
                      ? '#29A322'
                      : isDark
                        ? GlassColors.accent
                        : GlassColors.secondary,
                  }}
                >
                  {showSkeleton ? (
                    <Skeleton />
                  ) : (
                    (() => {
                      return userDetails?.isStripeAccountVerified
                        ? 'You have successfully completed Stripe verification!'
                        : 'Payout Verification (Stripe)';
                    })()
                  )}
                </Typography>
                <Typography
                  component='p'
                  sx={{
                    fontSize: 16,
                    fontWeight: 400,
                    fontFamily: 'Roboto, sans-serif',
                    lineHeight: '150%',
                    letterSpacing: '.15px',
                    color: isDark
                      ? GlassColors.dark.textSecondary
                      : GlassColors.light.textSecondary,
                  }}
                >
                  Stripe Connect is a global standard for secure online payments
                  and payouts. We use it to transfer your earnings directly to
                  your bank account safely and efficiently. You will be asked to
                  link a valid debit card or bank account to enable withdrawals.
                  Your financial details are never stored on our servers but are
                  handled exclusively by Stripe’s encrypted infrastructure to
                  ensure complete protection of your funds
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      p: '5.5px 16px',
                      '& svg path': {
                        fill: isDark
                          ? GlassColors.dark.textSecondary
                          : GlassColors.light.textSecondary,
                      },
                    }}
                  >
                    <LinkIcon />
                    <Link
                      component={RouterLink}
                      to='/help-blog/stripe-verification-update-for-service-providers'
                      sx={{
                        fontFamily: 'Montserrat, sans-serif',
                        fontWeight: 500,
                        fontSize: 14,
                        lineHeight: 1.2,
                        color: isDark
                          ? GlassColors.dark.textSecondary
                          : GlassColors.light.textSecondary,
                        '&:before': {
                          opacity: 1,
                          backgroundColor: isDark
                            ? GlassColors.dark.textSecondary
                            : GlassColors.light.textSecondary,
                        },
                      }}
                    >
                      Stripe verification
                    </Link>
                  </Box>

                  <GlassButton
                    disabled={
                      userDetails?.isStripeAccountVerified && !!authData
                    }
                    onClick={
                      !!authData
                        ? () => handleStripeClick()
                        : () =>
                            navigate('/sign-in', {
                              state: { from: location.pathname },
                            })
                    }
                    sx={{
                      '&&': {
                        height: '40px',
                        p: '6px 16px',
                        fontSize: 14,
                      },
                    }}
                  >
                    {stripeBusy ? (
                      <CircularProgress size={20} sx={{ color: '#E2F7FF' }} />
                    ) : (
                      <Typography
                        sx={{
                          fontFamily: 'Montserrat, sans-serif',
                          fontSize: 14,
                          fontWeight: 500,
                        }}
                      >
                        {userDetails?.isStripeAccountVerified && !!authData
                          ? 'Done'
                          : !!authData
                            ? 'Verify Now'
                            : 'Log in to verify'}
                      </Typography>
                    )}
                  </GlassButton>
                </Box>
              </Box>
            ))}
        </Box>
      </Box>
    </Box>
  );
};

export default VerificationPage;
